import React, { useEffect, useState } from 'react';
import { CheckCircle, Filter, Loader2, RefreshCw, RotateCcw, Tag, XCircle } from 'lucide-react';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import PaginationControls from '../../../../components/Shared/PaginationControls';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { Textarea } from '../../../../components/ui/textarea';
import { useToast } from '../../../../hooks/use-toast';
import {
  listProspectDiscoveryQueue,
  reviewProspectDiscoveryItem,
} from '../../../../services/AdminApis';
import api from '../../../../services/apiServices';

const LLM_STATE_BADGE = {
  NEEDS_REVIEW: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  UNPROCESSED: 'bg-gray-100 text-gray-700',
};

const LLM_STATE_OPTIONS = [
  { value: 'NEEDS_REVIEW', label: 'Needs review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'UNPROCESSED', label: 'Not processed' },
  { value: 'ALL', label: 'All states' },
];

// Fixed strings the relevancy workers write when the check could not run at all,
// as opposed to an actual model judgement. Shown muted so they read differently.
const SYSTEM_NOTES = new Set([
  'No result from LLM',
  'Unknown category',
  'No homepage text (fetch failed or JS-only)',
  'parse error',
  'missing from LLM response',
  'all models rate limited',
]);

const EMPTY_FILTERS = { state: 'NEEDS_REVIEW', country: 'ALL', category: null, serpProduct: 'ALL', q: '' };

const FilterBadge = ({ label, onClear }) => (
  <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 cursor-default">
    {label}
    <span onClick={onClear} className="ml-1 cursor-pointer hover:text-red-500 font-bold">
      ×
    </span>
  </Badge>
);

function isSystemNote(note) {
  if (!note) return false;
  return SYSTEM_NOTES.has(note) || note.startsWith('LLM error:');
}

export default function ProspectLlmReviewQueue() {
  const { toast } = useToast();
  const [countries, setCountries] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [isFiltered, setIsFiltered] = useState(false);
  const [stagedFilters, setStagedFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterVersion, setFilterVersion] = useState(0);

  // Review dialog
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewMode, setReviewMode] = useState(null); // APPROVE | REJECT | RECATEGORISE
  const [reviewCategory, setReviewCategory] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await api.get('/admin/common/active-countries');
        setCountries(response.data || []);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Failed to load countries.';
        toast({ variant: 'destructive', title: 'Unable to load countries', description: detail });
      }
    };
    loadCountries();
  }, []);

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await listProspectDiscoveryQueue({
        page: pagination.page,
        limit: pagination.limit,
        llm_state: appliedFilters.state,
        country_code: appliedFilters.country !== 'ALL' ? appliedFilters.country : undefined,
        category_id: appliedFilters.category?.id,
        serp_product: appliedFilters.serpProduct !== 'ALL' ? appliedFilters.serpProduct : undefined,
        q: appliedFilters.q?.trim() ? appliedFilters.q.trim() : undefined,
      });
      setRows(data?.items || []);
      setPagination((prev) => ({ ...prev, total: data?.total || 0 }));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load the review queue.';
      toast({ variant: 'destructive', title: 'Unable to load queue', description: detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters(stagedFilters);
    setIsFiltered(true);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setStagedFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setIsFiltered(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilterVersion((current) => current + 1);
  };

  // A category from one country is meaningless under another.
  const handleFilterCountryChange = (value) => {
    setStagedFilters((prev) => ({ ...prev, country: value, category: null }));
    setFilterVersion((current) => current + 1);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const openReview = (row, mode) => {
    setReviewRow(row);
    setReviewMode(mode);
    setReviewCategory(null);
    setReviewNotes('');
  };

  const closeReview = () => {
    setReviewRow(null);
    setReviewMode(null);
    setReviewCategory(null);
    setReviewNotes('');
  };

  const handleSubmitReview = async () => {
    if (!reviewRow || !reviewMode) return;

    const notes = reviewNotes.trim();
    if (reviewMode === 'REJECT' && !notes) {
      toast({
        variant: 'destructive',
        title: 'Notes required',
        description: 'A reason is required to reject a prospect.',
      });
      return;
    }
    if (reviewMode === 'RECATEGORISE' && !reviewCategory?.id) {
      toast({
        variant: 'destructive',
        title: 'Category required',
        description: 'Pick the category this prospect should be filed under.',
      });
      return;
    }

    const payload = { notes: notes || undefined };
    if (reviewMode === 'APPROVE') payload.decision = 'APPROVED';
    if (reviewMode === 'REJECT') payload.decision = 'REJECTED';
    if (reviewCategory?.id) payload.category_id = reviewCategory.id;

    setSubmitting(true);
    try {
      const updated = await reviewProspectDiscoveryItem(reviewRow.id, payload);

      // If the row no longer matches the active filter it leaves the list;
      // otherwise swap it in place with what the server actually stored.
      const stillMatches =
        appliedFilters.state === 'ALL' || updated.llm_state === appliedFilters.state;
      if (stillMatches) {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } else {
        setRows((prev) => prev.filter((r) => r.id !== updated.id));
        setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      }

      toast({
        title: 'Review saved',
        description:
          reviewMode === 'APPROVE'
            ? 'Approved — the row will be scraped on the next worker run.'
            : reviewMode === 'REJECT'
              ? 'Rejected — this row will not be scraped.'
              : `Recategorised to ${updated.category_name || 'the selected category'}.`,
      });
      closeReview();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save the review decision.';
      toast({ variant: 'destructive', title: 'Review not saved', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const reviewCountry = reviewRow?.country || '';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">LLM Review Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            Businesses the relevancy check could not decide on. Approving releases a row back into
            the scraper; rejecting drops it for good.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={loadRows} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500">LLM state</Label>
              <Select
                value={stagedFilters.state}
                onValueChange={(value) => setStagedFilters((prev) => ({ ...prev, state: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LLM_STATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[160px]">
              <Label className="text-xs text-gray-500">Country</Label>
              <Select value={stagedFilters.country} onValueChange={handleFilterCountryChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country.country_code} value={country.country_code}>
                      {country.name} ({country.country_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[220px]">
              <Label className="text-xs text-gray-500">Category</Label>
              <div className="mt-1">
                {stagedFilters.country !== 'ALL' ? (
                  <CategorySearch
                    key={`filter-cat-${stagedFilters.country}-${filterVersion}`}
                    locale={stagedFilters.country}
                    onSelect={(category) => setStagedFilters((prev) => ({ ...prev, category }))}
                    typeFilter="child"
                    placeholder={stagedFilters.category?.name || 'Filter by category...'}
                  />
                ) : (
                  <Input disabled placeholder="Select a country first" />
                )}
              </div>
            </div>

            <div className="w-[160px]">
              <Label className="text-xs text-gray-500">Search type</Label>
              <Select
                value={stagedFilters.serpProduct}
                onValueChange={(value) => setStagedFilters((prev) => ({ ...prev, serpProduct: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All types</SelectItem>
                  <SelectItem value="GOOGLE_MAPS">Google Maps</SelectItem>
                  <SelectItem value="GOOGLE_ORGANIC">Google Search</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-[220px]">
              <Label className="text-xs text-gray-500">Company or domain</Label>
              <Input
                className="mt-1"
                value={stagedFilters.q}
                onChange={(event) => setStagedFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="Search name or domain..."
              />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={handleApplyFilters}>
                <Filter size={16} className="mr-2" />
                Apply
              </Button>
              {isFiltered && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResetFilters}
                  className="text-red-600 hover:bg-red-50"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {isFiltered && (
            <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t">
              <span className="text-xs font-semibold text-gray-500">Filtered by:</span>
              <FilterBadge
                label={`State: ${LLM_STATE_OPTIONS.find((o) => o.value === appliedFilters.state)?.label}`}
                onClear={() => {
                  setStagedFilters((p) => ({ ...p, state: 'NEEDS_REVIEW' }));
                  setAppliedFilters((p) => ({ ...p, state: 'NEEDS_REVIEW' }));
                }}
              />
              {appliedFilters.country !== 'ALL' && (
                <FilterBadge
                  label={`Country: ${appliedFilters.country}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, country: 'ALL', category: null }));
                    setAppliedFilters((p) => ({ ...p, country: 'ALL', category: null }));
                  }}
                />
              )}
              {appliedFilters.category && (
                <FilterBadge
                  label={`Category: ${appliedFilters.category.name}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, category: null }));
                    setAppliedFilters((p) => ({ ...p, category: null }));
                  }}
                />
              )}
              {appliedFilters.serpProduct !== 'ALL' && (
                <FilterBadge
                  label={`Type: ${appliedFilters.serpProduct === 'GOOGLE_ORGANIC' ? 'Search' : 'Maps'}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, serpProduct: 'ALL' }));
                    setAppliedFilters((p) => ({ ...p, serpProduct: 'ALL' }));
                  }}
                />
              )}
              {appliedFilters.q?.trim() && (
                <FilterBadge
                  label={`Search: ${appliedFilters.q.trim()}`}
                  onClear={() => {
                    setStagedFilters((p) => ({ ...p, q: '' }));
                    setAppliedFilters((p) => ({ ...p, q: '' }));
                  }}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Discovered Businesses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Company</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Verdict</TableHead>
                  <TableHead>LLM note</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500 py-8">
                      {loading ? 'Loading...' : 'No businesses match these filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{row.company_name || 'Unknown'}</span>
                        {row.website_url ? (
                          <a
                            href={row.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {row.base_domain}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">{row.base_domain}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {[row.city, row.state].filter(Boolean).join(', ') || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{row.category_name || '-'}</span>
                        {row.external_category && (
                          <span className="text-xs text-gray-400">Google: {row.external_category}</span>
                        )}
                        {row.admin_original_category_name && (
                          <span className="text-xs text-purple-600">
                            was: {row.admin_original_category_name}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          row.serp_product === 'GOOGLE_ORGANIC'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-700'
                        }
                      >
                        {row.serp_product === 'GOOGLE_ORGANIC' ? 'Search' : 'Maps'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={LLM_STATE_BADGE[row.llm_state] || 'bg-gray-100 text-gray-700'}>
                          {row.llm_state.replace('_', ' ')}
                        </Badge>
                        {row.llm_confidence != null && (
                          <span className="text-xs text-gray-500">
                            conf {row.llm_confidence.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      <p
                        className={`text-xs whitespace-pre-wrap break-words ${
                          isSystemNote(row.llm_review_notes) ? 'text-gray-400 italic' : 'text-gray-700'
                        }`}
                      >
                        {row.llm_review_notes || '-'}
                      </p>
                      {row.admin_review_notes && (
                        <p className="text-xs text-purple-700 whitespace-pre-wrap break-words mt-1">
                          Admin: {row.admin_review_notes}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.contacts_promoted || row.promoted_prospect_id ? (
                        <span className="text-xs text-gray-400">Prospect already created</span>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50 hover:text-green-700"
                            onClick={() => openReview(row, 'APPROVE')}
                            title="Approve"
                            aria-label={`Approve ${row.company_name || row.base_domain}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => openReview(row, 'REJECT')}
                            title="Reject"
                            aria-label={`Reject ${row.company_name || row.base_domain}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                            onClick={() => openReview(row, 'RECATEGORISE')}
                            title="Change category"
                            aria-label={`Change category ${row.company_name || row.base_domain}`}
                          >
                            <Tag className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {rows.length > 0 && (
            <div className="pt-4 flex justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                Showing {rows.length} of {pagination.total} businesses
              </p>
              <PaginationControls
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(reviewRow)} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {reviewMode === 'APPROVE'
                ? 'Approve prospect'
                : reviewMode === 'REJECT'
                  ? 'Reject prospect'
                  : 'Change category'}
            </DialogTitle>
            <DialogDescription>
              {reviewMode === 'APPROVE'
                ? 'This releases the business back into the scraper, which will visit its site on the next run.'
                : reviewMode === 'REJECT'
                  ? 'This is final — the business will never be scraped or promoted.'
                  : 'Correct where this prospect gets filed without changing its approval state.'}
            </DialogDescription>
          </DialogHeader>

          {reviewRow && (
            <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
              <div className="rounded-md border p-3 space-y-1">
                <p className="font-medium">{reviewRow.company_name || 'Unknown'}</p>
                {reviewRow.website_url ? (
                  <a
                    href={reviewRow.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all"
                  >
                    {reviewRow.website_url}
                  </a>
                ) : (
                  <p className="text-sm text-gray-500">No website (phone-only lead)</p>
                )}
                <p className="text-sm text-gray-600">
                  {[reviewRow.city, reviewRow.state, reviewRow.country].filter(Boolean).join(', ') || '-'}
                  {reviewRow.phone ? ` · ${reviewRow.phone}` : ''}
                </p>
                {(reviewRow.rating != null || reviewRow.review_count != null) && (
                  <p className="text-sm text-gray-600">
                    {reviewRow.rating != null ? `★ ${reviewRow.rating}` : ''}
                    {reviewRow.review_count != null ? ` · ${reviewRow.review_count} reviews` : ''}
                  </p>
                )}
                {reviewRow.search_string && (
                  <p className="text-xs text-gray-400">
                    Found via: "{reviewRow.search_string}"
                    {reviewRow.serp_product
                      ? ` (${reviewRow.serp_product === 'GOOGLE_ORGANIC' ? 'Search' : 'Maps'})`
                      : ''}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs text-gray-500">LLM verdict</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={LLM_STATE_BADGE[reviewRow.llm_state] || 'bg-gray-100 text-gray-700'}>
                    {reviewRow.llm_state.replace('_', ' ')}
                  </Badge>
                  {reviewRow.llm_confidence != null && (
                    <span className="text-xs text-gray-500">
                      confidence {reviewRow.llm_confidence.toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-sm bg-muted rounded-md p-3 mt-2 whitespace-pre-wrap break-words">
                  {reviewRow.llm_review_notes || 'No reason recorded.'}
                </p>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Category</Label>
                <p className="text-sm mt-1">
                  Currently <span className="font-medium">{reviewRow.category_name || 'none'}</span>
                  {reviewRow.external_category && (
                    <span className="text-gray-500"> · Google called it "{reviewRow.external_category}"</span>
                  )}
                </p>
                <div className="mt-2">
                  {reviewCountry ? (
                    <CategorySearch
                      key={`review-cat-${reviewRow.id}`}
                      locale={reviewCountry}
                      onSelect={setReviewCategory}
                      typeFilter="child"
                      placeholder="Change category (optional)..."
                    />
                  ) : (
                    <Input disabled placeholder="No country on this row — cannot change category" />
                  )}
                </div>
                {reviewCategory && (
                  <p className="text-xs text-purple-600 mt-1">
                    Will be filed under: {reviewCategory.name} (ID: {reviewCategory.id})
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs text-gray-500">
                  Notes {reviewMode === 'REJECT' ? '(required)' : '(optional)'}
                </Label>
                <Textarea
                  className="mt-1"
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                  placeholder="Why did you make this decision?"
                  maxLength={500}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeReview} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmitReview} disabled={submitting}>
              {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              {reviewMode === 'APPROVE' ? 'Approve' : reviewMode === 'REJECT' ? 'Reject' : 'Save category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
